import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import QuoteModal from "@/components/QuoteModal";
import brightonKitchenWide from "@/assets/reclaimed-tobacco-pine-floor-kitchen-wide-howell-mi.webp";
import commercialAnnArbor from "@/assets/commercial-ann-arbor.webp";
import harborSpringsMaple from "@/assets/harbor-springs-invisible-maple-vaulted.webp";
import northvilleWalnut from "@/assets/custom-8-inch-walnut-fas-northville-mi.webp";
const staircaseHero = "/images/site/red-oak-staircase-dusky-walnut-floor-catwalk-view-farmington-hills-mi.webp";
import plymouthCustomOak from "@/assets/1-wood-custom-line-8-inch-white-oak-plymouth-mi.webp";

const projects = [
  { img: brightonKitchenWide, alt: "Reclaimed antique Tobacco Pine floor with original saw marks, supplied and installed by 1 Wood Floors, Howell, MI", title: "Antique Reclaimed Tobacco Pine", subtitle: "Reclaimed antique Tobacco Pine with every original saw mark and imperfection kept, supplied and installed by 1 Wood Floors in November 2019.", location: "Howell, MI", slug: "/projects/howell-reclaimed-tobacco-pine", span: "md:col-span-2 md:row-span-2" },
  { img: commercialAnnArbor, alt: "Commercial staircase and engineered oak floor refinished with Pallmann Magic Oil, Ann Arbor, MI", title: "Commercial Dustless Refinishing", subtitle: "A high pressure weekend turnaround  we restored a delicate 2mm sawn face engineered oak with 98% dust containment and zero disruption.", location: "Ann Arbor, Michigan", slug: "/projects/ann-arbor-tech", span: "md:col-span-2" },
  { img: northvilleWalnut, alt: "Open kitchen with an 8 inch engineered FAS walnut floor and a matching floating staircase, Northville, MI", title: "8\" FAS Custom Walnut", subtitle: "Our most luxurious expression  bespoke 8-inch FAS Walnut planks integrated with a solid Walnut floating staircase.", location: "Northville, Michigan", slug: "/projects/northville-walnut", span: "md:row-span-2" },
  { img: plymouthCustomOak, alt: "Kitchen with an 8 inch engineered 1 Wood Floors Custom Line white oak floor in Sand, Plymouth, MI", title: "8\" Custom White Oak", subtitle: "A bespoke 'Sand' finish engineered in our Custom Lab  with a massive 4.5mm sawn face built for generations.", location: "Plymouth, Michigan", slug: "/projects/plymouth-custom-white-oak", badge: "Michigan Plank Flooring", span: "" },
  { img: staircaseHero, alt: "Catwalk view of a red oak staircase stained to match a hand scraped Dusky Walnut floor, Farmington Hills, MI", title: "Staircase Renovations", subtitle: "A three specialist symphony  floor specialist, finish carpenter, and fine finish painter.", location: "Michigan (Statewide)", slug: "/staircase-renovations", span: "md:col-span-2" },
  { img: harborSpringsMaple, alt: "Vaulted room with a maple floor in an invisible finish, Harbor Springs, MI", title: "The Harbor Springs Minimalist", subtitle: "Scandinavian inspired luxury  an advanced \"Invisible\" finish preserves the natural beauty of Northern Maple.", location: "Harbor Springs, Michigan", badge: "Invisible Finish Specialist", slug: "/projects/harbor-springs-maple", span: "" },
];

const RecentProjects = () => (
  <section className="bg-background py-14 md:py-20 lg:py-28" id="projects-gallery">
    <div className="max-w-[1200px] mx-auto px-5 md:px-12">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 md:mb-10"
      >
        <div>
          <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-3">
            Recent Projects
          </p>
          <h2 className="font-display text-[clamp(28px,5vw,52px)] font-semibold text-foreground leading-[1.1]">
            Work we're <em className="italic font-normal text-primary">proud of.</em>
          </h2>
        </div>
        <QuoteModal
          trigger={
            <button className="mt-4 md:mt-0 inline-flex items-center gap-2 border border-border text-foreground px-6 py-3 rounded-full text-xs font-medium hover:border-primary/30 hover:shadow-md transition-all self-start md:self-auto">
              Discuss Your Project →
            </button>
          }
        />
      </motion.div>

      {/* Seamless Mosaic */}
      <div className="rounded-[28px] overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-[4px]" style={{ gridAutoFlow: "dense", gridAutoRows: "minmax(220px, 1fr)" }}>
          {projects.map((p, i) => {
            const isLarge = p.span.includes("col-span-2") && p.span.includes("row-span-2");

            const Tag = p.slug ? Link : "div";
            const tagProps = p.slug ? { to: p.slug } : {};

            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                className={`relative overflow-hidden group min-h-[260px] md:min-h-0 ${p.span}`}
              >
                <Tag {...tagProps as any} className="absolute inset-0 block">
                  <img width={800} height={534} loading="lazy"
                    src={p.img}
                    alt={p.alt}
                    className={`absolute inset-0 w-full h-full ${p.img === brightonKitchenWide ? "object-cover object-center" : "object-cover group-hover:scale-[1.05]"} grayscale-[20%] contrast-[1.1] group-hover:grayscale-0 transition-all duration-[550ms] ease-[cubic-bezier(.25,.46,.45,.94)]`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                  <div className={`absolute bottom-0 left-0 ${isLarge ? "p-5 md:p-7" : "p-4 md:p-5"}`}>
                    {p.badge && (
                      <p className="text-[11px] md:text-[10px] font-medium tracking-[.14em] uppercase text-primary-foreground/50 mb-1">{p.badge}</p>
                    )}
                    <h3 className={`font-display font-semibold text-primary-foreground leading-tight ${isLarge ? "text-lg md:text-[26px]" : "text-base md:text-base"}`}>
                      {p.title}
                    </h3>
                    {p.subtitle && (
                      <p className={`font-light text-primary-foreground/70 mt-1 leading-relaxed ${isLarge ? "text-sm md:text-sm max-w-sm" : "text-[14px] md:text-[11px] max-w-xs"}`}>
                        {p.subtitle}
                      </p>
                    )}
                    <p className={`tracking-[.12em] uppercase text-primary-foreground/70 mt-1 ${isLarge ? "text-[11px] md:text-xs" : "text-[11px] md:text-[10px]"}`}>
                      {p.location}
                    </p>
                    {p.slug && (
                      <span className="inline-flex items-center gap-1 text-[14px] md:text-[11px] font-semibold tracking-wide text-gold-400 mt-2 group-hover:text-gold-300 transition-colors">
                        View Project <ArrowRight className="w-3 h-3 transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    )}
                  </div>
                </Tag>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

export default RecentProjects;
